/**
 * Bounded list fetching for PostgREST (F2L-27).
 * Pages through results so callers still receive the full permitted set without
 * a single unbounded select, while enforcing a hard ceiling against abuse.
 */

export const DEFAULT_LIST_PAGE_SIZE = 500;
/** Absolute ceiling — prevents runaway memory if a tenant is unexpectedly huge. */
export const HARD_LIST_MAX_ROWS = 10_000;

export type PageFetcher<T> = (from: number, to: number) => Promise<T[]>;

/** Ceiling for org-wide fetches that replace per-employee queries (large orgs exceed 10k rows). */
export const ORG_WIDE_LIST_MAX_ROWS = 200_000;

/**
 * {@link fetchAllPagesBounded} for org-wide reads: higher ceiling, one retry per page so a
 * transient failure does not blank the whole org, and a console warning if still truncated.
 */
export async function fetchAllPagesOrgWide<T>(label: string, fetchPage: PageFetcher<T>): Promise<T[]> {
  const { rows, truncated } = await fetchAllPagesBounded<T>(
    async (from, to) => {
      try {
        return await fetchPage(from, to);
      } catch {
        return await fetchPage(from, to);
      }
    },
    { hardMax: ORG_WIDE_LIST_MAX_ROWS },
  );
  if (truncated) {
    console.warn(`[fetchAllPagesOrgWide] ${label} truncated at ${ORG_WIDE_LIST_MAX_ROWS} rows`);
  }
  return rows;
}

/**
 * Fetch pages until a short page or HARD_LIST_MAX_ROWS.
 * Does not silently truncate without signaling: returns `{ truncated: true }` when the hard max is hit.
 */
export async function fetchAllPagesBounded<T>(
  fetchPage: PageFetcher<T>,
  opts?: { pageSize?: number; hardMax?: number },
): Promise<{ rows: T[]; truncated: boolean; pageSize: number }> {
  const pageSize = opts?.pageSize ?? DEFAULT_LIST_PAGE_SIZE;
  const hardMax = opts?.hardMax ?? HARD_LIST_MAX_ROWS;
  const rows: T[] = [];
  let truncated = false;

  for (let from = 0; from < hardMax; from += pageSize) {
    const to = Math.min(from + pageSize - 1, hardMax - 1);
    const page = await fetchPage(from, to);
    rows.push(...page);
    if (page.length < pageSize) {
      return { rows, truncated, pageSize };
    }
    if (rows.length >= hardMax) {
      truncated = true;
      return { rows: rows.slice(0, hardMax), truncated, pageSize };
    }
  }

  truncated = rows.length >= hardMax;
  return { rows, truncated, pageSize };
}
